import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Camera, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const DMCAPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="DMCA Copyright Policy | 360Lumina"
        description="Read 360Lumina's DMCA Copyright Policy. Learn about our copyright infringement procedures and how to file a takedown notice."
        canonical="https://www.360lumina.com/dmca"
      />
      {/* Header */}
      <header className="border-b border-border py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Camera className="w-6 h-6 text-primary" />
            <span className="text-xl font-serif">360Lumina</span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-serif mb-2">DMCA Copyright Policy</h1>
        <p className="text-muted-foreground mb-8">Effective Date: January 1, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <p>
            360Lumina LLC ("360Lumina," "we," "us," or "our") respects the intellectual property rights of others and 
            expects users of the Platform to do the same. This Digital Millennium Copyright Act ("DMCA") Policy describes 
            how we respond to claims of copyright infringement.
          </p>

          <section>
            <h2 className="text-2xl font-serif mb-4">1. Copyright Infringement Notification</h2>
            <p>
              If you believe that your copyrighted work has been copied, displayed, distributed, or otherwise made available 
              on the Platform in a manner that constitutes copyright infringement, you may submit a written notification 
              ("DMCA Notice") to our designated Copyright Agent. Your DMCA Notice must include all of the following information 
              as required by 17 U.S.C. § 512(c)(3):
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>A physical or electronic signature of the copyright owner or a person authorized to act on their behalf;</li>
              <li>
                Identification of the copyrighted work claimed to have been infringed (or a representative list if multiple 
                works are involved);
              </li>
              <li>
                Identification of the allegedly infringing material and sufficient information to allow us to locate it on 
                the Platform (e.g., specific URLs);
              </li>
              <li>Your name, mailing address, telephone number, and email address;</li>
              <li>
                A statement that you have a good-faith belief that use of the material is not authorized by the copyright 
                owner, its agent, or the law;
              </li>
              <li>
                A statement, under penalty of perjury, that the information in the notification is accurate and that you 
                are the copyright owner or authorized to act on the owner's behalf.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">2. Designated Copyright Agent</h2>
            <p>DMCA Notices should be sent to:</p>
            <div className="bg-muted/30 p-4 rounded-lg mt-4">
              <p className="font-medium">Copyright Agent</p>
              <p>360Lumina LLC</p>
              <p>
                Email:{" "}
                <a href="mailto:support@360lumina.com" className="text-primary hover:underline">
                  support@360lumina.com
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">3. Response to Valid DMCA Notices</h2>
            <p>Upon receipt of a valid DMCA Notice, 360Lumina may, at its discretion:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Remove or disable access to the allegedly infringing material;</li>
              <li>Notify the user who uploaded the material;</li>
              <li>Take additional action as required by law.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">4. Counter-Notification</h2>
            <p>
              If you believe that the material removed or disabled was removed as a result of mistake or misidentification, 
              you may submit a counter-notification ("DMCA Counter-Notice"). Your counter-notification must include:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Your physical or electronic signature;</li>
              <li>Identification of the material that was removed or disabled and the location where it appeared before removal;</li>
              <li>
                A statement under penalty of perjury that you have a good-faith belief that the material was removed due to 
                mistake or misidentification;
              </li>
              <li>Your name, address, telephone number, and email address;</li>
              <li>
                A statement consenting to the jurisdiction of the federal district court for your address (or, if outside the U.S., 
                the jurisdiction where 360Lumina is located) and agreeing to accept service of process from the complaining party.
              </li>
            </ul>
            <p className="mt-4">Counter-notifications should be sent to the same Copyright Agent listed above.</p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">5. Repeat Infringer Policy</h2>
            <p>
              In accordance with the DMCA and other applicable laws, 360Lumina has adopted a policy of terminating, in 
              appropriate circumstances and at our sole discretion, the accounts of users who are deemed to be repeat infringers.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">6. Misrepresentations</h2>
            <p>
              Any person who knowingly materially misrepresents that material or activity is infringing, or that material 
              or activity was removed or disabled by mistake or misidentification, may be subject to liability under applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">7. Modifications to This Policy</h2>
            <p>
              360Lumina reserves the right to modify this DMCA Policy at any time. Changes will be effective upon posting to the Platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">8. Minors and Guardian Rights</h2>
            <p>
              360Lumina recognizes that some content hosted on the Platform may depict minors. Legal guardians or parents 
              who believe content depicting a minor has been uploaded without proper authorization may submit a DMCA Notice 
              or removal request in accordance with this Policy.
            </p>
            <p className="mt-4">
              In addition to copyright claims, 360Lumina may remove content depicting minors where required to comply with 
              privacy, publicity, or child protection laws. 360Lumina reserves the right to remove content depicting minors 
              at its discretion, even in the absence of a formal DMCA Notice, where continued hosting may pose legal or safety risks.
            </p>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-12 pt-8 border-t border-border">
          <h3 className="text-lg font-medium mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
            <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DMCAPolicy;
